import Head from 'next/head';
import PropTypes from 'prop-types';
import { NextSeo } from 'next-seo';
import pushRoute from '@utils/pushRoute';
import EnrolledCourse from '@cont/EnrolledCourse/EnrolledCourse';
import {
  fetchEnrolledCourses,
  fetchEnrolledCoursesFilters
} from '@actions/courses';
import { fetchTopologyInfo } from '@actions/topologies';
import { useSetSelectedNav } from '@stores/sidenav';
import { ENROLLMENTS } from '@constants/segment';
import setupInitialProps from '@utils/setupInitialProps';
import { redirectToRoute } from '@utils/route';
import Layout from '@layout/index';
import PageReduxProvider from '@cont/App/PageReduxProviderWrapper';

const EnrolledCoursesSeeAllPage = ({ goalInfo }) => {
  const { title } = goalInfo || {};
  const metaTitle = `My Enrollments | ${title} | Unacademy`;

  useSetSelectedNav('enrolled');

  return (
    <>
      <NextSeo
        title={metaTitle}
        openGraph={{
          title: metaTitle
        }}
      />
      <Head>
        <title>{metaTitle}</title>
      </Head>
      <EnrolledCourse seeAllType="enrolledCourses" />
    </>
  );
};

EnrolledCoursesSeeAllPage.pageInfo = {
  isMWebSupported: true,
  name: ENROLLMENTS,
  layoutOptions: {
    showPlusNav: true
  }
};

export default EnrolledCoursesSeeAllPage;

EnrolledCoursesSeeAllPage.getInitialProps = setupInitialProps(
  async ({ store, query, isServer, res }) => {
    const { goalUID } = query;
    /**
     * We have different dropdowns on enrolled page. Here `all` shows that all the values in dropdown are selected.
     * 4 all stands for state, language, educator and topic.
     */
    const key = `${goalUID}-enrolled-all-all-all-all`;
    const promises = [];
    const goalInfo = store.getState().topologies.data[goalUID];

    if (!store.getState().topologies?.topologyDetails?.[goalUID]) {
      promises.push(store.dispatch(fetchTopologyInfo(goalUID)));
    }
    if (!store.getState().paginator?.enrolledCourses?.[key]) {
      promises.push(
        store.dispatch(
          fetchEnrolledCourses(
            goalUID,
            {
              state: 'all',
              language: 'all',
              educator: 'all',
              topic: 'all'
            },
            false
          )
        )
      );
    }
    promises.push(store.dispatch(fetchEnrolledCoursesFilters(goalUID)));
    if (promises.length) await Promise.all(promises);
    if (goalInfo?.isPlatformGoal) {
      const { goalSlug } = goalInfo;
      redirectToRoute(
        isServer,
        res,
        `/goal/${goalSlug}/${goalUID}/batch-group/home`
      );
    }
    if (query && goalInfo && query.goalSlug !== goalInfo.goalSlug) {
      if (isServer) {
        res.writeHead(302, {
          Location: `/goal/${goalInfo.goalSlug}/${goalUID}/enrolled`
        });
        return res.end();
      }
      return pushRoute(
        '/goal/[goalSlug]/[goalUID]/enrolled',
        `/goal/${goalInfo.goalSlug}/${goalUID}/enrolled`
      );
    }
    return { isServer, goalInfo };
  },
  EnrolledCoursesSeeAllPage.pageInfo
);

EnrolledCoursesSeeAllPage.propTypes = {
  goalInfo: PropTypes.objectOf(PropTypes.any).isRequired
};
EnrolledCoursesSeeAllPage.Layout = Layout;
EnrolledCoursesSeeAllPage.Wrapper = PageReduxProvider;
